import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'

export type OfferTilePriceBlockVariation = 'variant-a' | 'variant-b'

export interface OfferTilePriceBlockEligibility {
  type?: string
  bundledWithFlightsOnly?: boolean | null
  offerFlightsEnabled?: boolean
  campaignTags?: Array<string>
  holidayTypes?: Array<string>
  forceBundleId?: string
}

type PriceBlockLabelLayout = 'luxplus-savings' | 'percentage-blue' | 'percentage-green'

/**
 * The treatment is deliberately opt-in: an incomplete offer shape cannot
 * establish that the displayed price is hotel-only, so it remains control.
 */
export function isEligibleOfferTilePriceBlock(offer: OfferTilePriceBlockEligibility | undefined, hotelOnlyPrice?: boolean): boolean {
  if (!offer || offer.bundledWithFlightsOnly || offer.forceBundleId) return false

  // null is the API's valid unbundled value. Missing data is not the same thing.
  const hasHotelOnlyPrice = hotelOnlyPrice ?? (offer.bundledWithFlightsOnly === false || offer.bundledWithFlightsOnly === null)
  if (!hasHotelOnlyPrice) return false

  const isHotelType = offer.type === OFFER_TYPE_HOTEL ||
    offer.type === OFFER_TYPE_ALWAYS_ON ||
    offer.type === OFFER_TYPE_BED_BANK
  const isMaldivesCharter = offer.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  // Legacy cruises can use the hotel offer type. Keep the exclusion shared by
  // search, homepage and recommendation tiles (CRO-7023).
  const isLegacyCruise = offer.holidayTypes?.some(holidayType => holidayType.toLowerCase() === 'cruises')

  return isHotelType && !isMaldivesCharter && !isLegacyCruise
}

export function getOfferTilePriceBlockLabelLayout(
  offer: OfferTilePriceBlockEligibility | undefined,
  variation: OfferTilePriceBlockVariation | undefined,
  displayedMode: App.LuxPlusPriceStackMode,
  canViewMemberBenefits = false,
): PriceBlockLabelLayout | undefined {
  if (!variation || offer?.type === OFFER_TYPE_BED_BANK) return undefined

  if (displayedMode === 'member-only') {
    if (variation === 'variant-a') return 'luxplus-savings'
    return canViewMemberBenefits ? 'percentage-blue' : 'percentage-green'
  }

  if (variation === 'variant-a' && (displayedMode === 'member-prominent' || displayedMode === 'regular-prominent')) {
    return 'luxplus-savings'
  }

  if (variation === 'variant-b' && displayedMode === 'member-prominent') return 'percentage-blue'
  if (variation === 'variant-b' && displayedMode === 'regular-prominent') return 'percentage-green'
  if (offer?.type === OFFER_TYPE_ALWAYS_ON && displayedMode === 'regular-only') return 'percentage-green'
}
